import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, TextArea, Select, FormField, Alert } from '../ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface GoalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onGoalCreated: (goal: any) => void;
  initialGoal?: any;
  isEditMode?: boolean;
}

const initialFormData = {
    title: '',
    description: '',
    status: 'planning', // Default status
    target_date: '',
};

const GoalForm: React.FC<GoalFormProps> = ({
    isOpen,
    onClose,
    onGoalCreated,
    initialGoal = null,
    isEditMode = false
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Reset form when modal opens/closes or initial data changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialGoal) {
        // Format target_date for date input (YYYY-MM-DD)
        const formattedDate = initialGoal.target_date
          ? initialGoal.target_date.split('T')[0] // Take only date part if ISO
          : '';
        setFormData({ ...initialGoal, target_date: formattedDate });
      } else {
        setFormData(initialFormData);
      }
    }
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300)
    }
  }, [isOpen, isEditMode, initialGoal]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const payload: any = { ...formData };
    // Remove optional fields if empty
    if (!payload.description) delete payload.description;
    if (!payload.target_date) delete payload.target_date;
    else {
      // Ensure date is in YYYY-MM-DD format
      payload.target_date = payload.target_date.split('T')[0];
    }

    try {
      let response;
      if (isEditMode && initialGoal) {
        response = await apiClient.patch(`/api/goals/${initialGoal.id}/`, payload);
      } else {
        response = await apiClient.post('/api/goals/', payload);
      }

      if (response.status >= 200 && response.status < 300) {
        setSubmitSuccess(true);
        onGoalCreated(response.data); // Pass the created/updated goal back
        if (!isEditMode) {
          setFormData(initialFormData);
        }
        setTimeout(() => { onClose(); }, 1500);
      } else {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} goal (${response.status})`);
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} goal:`, err);
      const errorMsg = err.message || `Failed to ${isEditMode ? 'update' : 'create'} goal.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) { onClose(); }
  }, [isSubmitting, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all 150ms ease-in-out',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Goal' : 'Create New Goal'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      opacity: isSubmitting ? 0.5 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                    }}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <FormField label="Title" htmlFor="title" required>
                    <Input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </FormField>

                  <FormField label="Description" htmlFor="description">
                    <TextArea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </FormField>

                  <FormField label="Status" htmlFor="status">
                    <Select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="planning">Planning</option>
                      <option value="active">Active</option>
                      <option value="on_hold">On Hold</option>
                      <option value="achieved">Achieved</option>
                    </Select>
                  </FormField>

                  <FormField label="Target Date" htmlFor="target_date">
                    <Input
                      type="date"
                      id="target_date"
                      name="target_date"
                      value={formData.target_date}
                      onChange={handleInputChange}
                    />
                  </FormField>

                  <div style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                    {submitSuccess && (
                      <Alert variant="success">
                        Goal {isEditMode ? 'updated' : 'created'}!
                      </Alert>
                    )}
                    {submitError && (
                      <Alert variant="error">
                        {submitError}
                      </Alert>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[12] }}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSubmitting}
                        loadingText={isEditMode ? 'Updating...' : 'Creating...'}
                      >
                        {isEditMode ? 'Update Goal' : 'Create Goal'}
                      </Button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GoalForm;
